import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  AccountCreationContainer,
  AssociationContainer,
  AccountCreationForm,
  AccountConnectContainer,
  AccountConnectCancellation,
  AccountCreationSuccess,
} from "../StyledComponents/AccountCreationStyles";
import {
  QRCode,
  LoadingSpinnerContainer,
  LoadingSpinner,
  ButtonDark,
  ButtonLight,
  InputWrapper,
  RedAsterisk,
} from "../StyledComponents/AppStyles";

export default function AccountCreationPage({
  connection,
  credentials,
  createInvitation,
  fetchVerification,
  issueCredentials,
  invitation,
  resetAppState,
  setUserData,
  userData,
  verifiedCredAttributes,
}) {
  const effectRan = useRef(false);
  const navigate = useNavigate();

  // pageState - "association", "form", "connect", "success"
  const [pageState, setPageState] = useState("association");

  const [association, setAssociation] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accEstablishedDate, setAccEstablishedDate] = useState("");

  const [toggleCancellation, setToggleCancellation] = useState(false);

  const formFilled = Object.entries(userData).every(([key, value]) => {
    if (key === "accountId" || key === "accountEstablishedDate") {
      return true;
    } else if (value !== "") {
      return true;
    } else {
      return false;
    }
  });

  const handleInputs = (e) => {
    let { name, value } = e.target;
    let formattedValue = value;

    if (name === "phoneNumber") {
      formattedValue = e.target.value.replace(
        /(\d{3})(\d{3})(\d{4})/,
        "$1-$2-$3"
      );
    }

    setUserData({ ...userData, [name]: formattedValue });
  };

  const handleKYCIssuance = () => {
    navigate("/account");
    issueCredentials("KYC", {
      surnames: verifiedCredAttributes.surname,
      given_names: verifiedCredAttributes.given_names,
      date_of_birth: verifiedCredAttributes.date_of_birth,
      gender_legal: verifiedCredAttributes.sex,
      photograph: "N/A",
      email: userData.email,
      mobile_phone_number: userData.phoneNumber,
      street_address: "1890 Fairway Drive",
      city: verifiedCredAttributes.city,
      state_province_region: verifiedCredAttributes.state_province_region,
      postal_code: verifiedCredAttributes.postal_code,
      proof_of_address_verified: "Yes",
      proof_of_address_method: "Passport",
      government_id_type: "Citizen",
      government_id_number: "11224314",
      government_id_expiration: verifiedCredAttributes.date_of_expiration,
      accounts_verified: "Yes",
      accounts_sum_of_balances_current: "780000",
      accounts_sum_of_balances_6_month_average: "170000",
      employment_current_verified: "Yes",
      employment_current_employer: "Apple Inc.",
      employment_current_start_date: "2020-10-05",
      employment_previous_verified: "Yes",
      employment_previous_employer: "Qualtrics",
      employment_previous_start_date: "2018-04-29",
      income_verified: "Yes",
      income_annual: "199000",
      income_currency: "USD",
      credit_score: "792",
      credit_score_agency: "Equifax",
      risk_rating: "4",
      blacklists_checked: "Yes",
      sdd_performed: "Yes",
      edd_performed: "Yes",
      credential_issued_date: "2021-09-07",
    });

    setPageState("association");
  };

  // useEffects
  useLayoutEffect(() => {
    if (process.env.NODE_ENV === "development") {
      if (!effectRan.current) {
        effectRan.current = true;
      } else {
        return () => {
          effectRan.current = false;
        };
      }
    }

    resetAppState();
    setPageState("association");
    setAssociation("");
    setAccountId("");
    setAccEstablishedDate("");
  }, []);

  useEffect(() => {
    if (pageState === "association") {
      return;
    } else if (
      !connection &&
      Object.keys(invitation).length === 0 &&
      pageState === "connect"
    ) {
      createInvitation();
    } else if (
      connection &&
      Object.keys(invitation).length !== 0 &&
      Object.keys(verifiedCredAttributes).length === 0 &&
      pageState === "connect"
    ) {
      fetchVerification("DRIVERS_LICENSE");
    } else if (
      connection &&
      Object.keys(verifiedCredAttributes).length !== 0 &&
      Object.keys(credentials).length === 0 &&
      pageState === "connect"
    ) {
      const accId = `${Math.floor(Math.random() * 90000) + 10000}`;
      const currentDate = Math.floor(new Date().getTime() / 1000).toString();

      setAccountId(accId);
      setAccEstablishedDate(currentDate);
      issueCredentials("BANKING_ACCOUNT", {
        account_id: accId,
        account_established_date: currentDate,
        username: userData.userId,
        institution_id: "12345",
        institution_name: "Gold Standard",
        surnames: verifiedCredAttributes.surname,
        given_names: verifiedCredAttributes.given_names,
        date_of_birth: verifiedCredAttributes.date_of_birth,
        email: userData.email,
        phone_number: userData.phoneNumber,
        account_access_type: association || "",
        credential_issued_date: currentDate,
      });
    } else if (
      connection &&
      pageState !== "success" &&
      Object.keys(verifiedCredAttributes).length !== 0 &&
      Object.keys(credentials).length !== 0
    ) {
      setUserData({
        ...userData,
        givenName: verifiedCredAttributes.given_names,
        familyName: verifiedCredAttributes.surname,
        dateOfBirth: verifiedCredAttributes.date_of_birth,
        accountId: accountId,
        accountEstablishedDate: accEstablishedDate,
      });
      setPageState("success");
    }
  }, [
    connection,
    credentials,
    invitation,
    pageState,
    userData,
    verifiedCredAttributes,
  ]);

  useEffect(() => {
    let successTimeout;
    if (pageState === "success") {
      // Simulate a loading time before redirecting to the accounts dashboard
      successTimeout = setTimeout(() => {
        handleKYCIssuance();
      }, 10000);
    }

    return () => {
      clearTimeout(successTimeout);
    };
  }, [pageState, userData, verifiedCredAttributes]);

  // Handler functions
  const handleSubmit = (e) => {
    e.preventDefault();

    setPageState("connect");
  };

  const handlePageReset = () => {
    setUserData({
      givenName: "",
      familyName: "",
      userId: "",
      dateOfBirth: "",
      phoneNumber: "",
      email: "",
      accountId: "",
      accountEstablishedDate: "",
    });
    setPageState("association");
    navigate("/");
  };

  const handleGoBack = () => {
    setAssociation("");
    setPageState("association");
  };

  // List of displays corresponding to pageState
  const displayAssociation = (
    <>
      <h2>Account Creation</h2>
      <p>
        Before we begin, please specify the nature of your association with the
        Indicio Banking Demo.
      </p>
      <AssociationContainer>
        <span>
          <input
            name="association"
            type="radio"
            id="customer"
            value={"customer"}
            onChange={(e) => setAssociation(e.target.value)}
          />
          <label htmlFor="customer">
            I am a <b>customer</b>
          </label>
        </span>
        <span>
          <input
            name="association"
            type="radio"
            id="employee"
            value={"employee"}
            onChange={(e) => setAssociation(e.target.value)}
          />
          <label htmlFor="employee">
            I am an <b>employee</b>
          </label>
        </span>
        <ButtonDark
          disabled={!association}
          onClick={() => setPageState("form")}
        >
          Continue
        </ButtonDark>
      </AssociationContainer>
    </>
  );

  const displayForm = (
    <>
      <h2>Account Creation</h2>
      <p>
        Welcome to the Indicio Banking Demo. Please tell us a little about
        yourself.
      </p>
      <p>
        <b>All required fields are marked with an asterisk.</b>{" "}
        <RedAsterisk>*</RedAsterisk>
      </p>
      <AccountCreationForm onSubmit={handleSubmit}>
        <InputWrapper>
          <label htmlFor="givenName">
            Given name(s) <RedAsterisk>*</RedAsterisk>
          </label>
          <input
            required
            type="text"
            value={userData.givenName}
            id="given-name"
            name="givenName"
            onChange={(e) => handleInputs(e)}
          />
        </InputWrapper>
        <InputWrapper>
          <label htmlFor="familyName">
            Family name(s) <RedAsterisk>*</RedAsterisk>
          </label>
          <input
            required
            type="text"
            value={userData.familyName}
            id="family-name"
            name="familyName"
            onChange={(e) => handleInputs(e)}
          />
        </InputWrapper>
        <InputWrapper>
          <label htmlFor="userId">
            User ID <RedAsterisk>*</RedAsterisk>
          </label>
          <input
            required
            type="text"
            value={userData.userId}
            id="user-id"
            name="userId"
            onChange={(e) => handleInputs(e)}
          />
          <p>You will use this to log into your account.</p>
        </InputWrapper>
        <div>
          <InputWrapper>
            <label htmlFor="dateOfBirth">
              Date of birth <RedAsterisk>*</RedAsterisk>
            </label>
            <input
              required
              type="date"
              value={userData.dateOfBirth}
              id="date-of-birth"
              name="dateOfBirth"
              onChange={(e) => handleInputs(e)}
            />
          </InputWrapper>
          <InputWrapper>
            <label htmlFor="phoneNumber">
              Phone number <RedAsterisk>*</RedAsterisk>
            </label>
            <input
              required
              type="tel"
              pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
              value={userData.phoneNumber}
              id="phone-number"
              name="phoneNumber"
              onChange={(e) => handleInputs(e)}
            />
          </InputWrapper>
        </div>
        <InputWrapper>
          <label htmlFor="email">
            Email address <RedAsterisk>*</RedAsterisk>
          </label>
          <input
            required
            type="email"
            value={userData.email}
            id="email"
            name="email"
            onChange={(e) => handleInputs(e)}
          />
        </InputWrapper>
        <span>
          <input type="checkbox" id="newsletter" name="newsletter" />
          <label htmlFor="newsletter">
            I would like to receive news and promotional emails regarding the
            Indicio Banking Demo.
          </label>
        </span>
        <div>
          <ButtonDark disabled={formFilled ? false : true} type="submit">
            Continue
          </ButtonDark>
          <p onClick={handleGoBack}>Go Back</p>
        </div>
      </AccountCreationForm>
    </>
  );

  const displayConnect = (
    <>
      <h2>Almost done</h2>
      <p>
        The Indicio Banking Demo allows customers to log into their accounts
        securely using verifiable credentials.
      </p>
      <p>
        <b>Scan the QR code below to receive a credential.</b>
      </p>
      <AccountConnectContainer>
        {!invitation?.invitation_url || connection ? (
          <LoadingSpinnerContainer>
            <LoadingSpinner />
            <p>Please wait...</p>
          </LoadingSpinnerContainer>
        ) : toggleCancellation ? (
          <AccountConnectCancellation>
            <p>Are you sure you want to cancel account creation?</p>
            <span>
              <p>
                You will need to re-enter all of your information next time you
                want to create an account.
              </p>
            </span>
            <div>
              <ButtonLight onClick={handlePageReset}>Yes, cancel</ButtonLight>
              <ButtonDark
                onClick={() => setToggleCancellation(!toggleCancellation)}
              >
                No, stay here
              </ButtonDark>
            </div>
          </AccountConnectCancellation>
        ) : (
          <>
            <QRCode value={invitation?.invitation_url} renderAs="svg" />
            <ButtonLight
              onClick={() => setToggleCancellation(!toggleCancellation)}
            >
              Cancel
            </ButtonLight>
          </>
        )}
      </AccountConnectContainer>
    </>
  );

  const displaySuccess = (
    <AccountCreationSuccess>
      <h2>Success!</h2>
      <p>
        Your account has successfully been created. You will now be redirected
        to your dashboard.
      </p>
      <p>
        If you are not redirected within 60 seconds,{" "}
        <u onClick={handleKYCIssuance}>click here.</u>
      </p>
    </AccountCreationSuccess>
  );

  return (
    <AccountCreationContainer>
      {pageState === "association" && displayAssociation}
      {pageState === "form" && displayForm}
      {pageState === "connect" && displayConnect}
      {pageState === "success" && displaySuccess}
    </AccountCreationContainer>
  );
}
